import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { useTranslation } from "react-i18next";
import { Popover as HeadlessPopover } from "@/components/ui/popover";

interface Company {
  id: string;
  name: string;
}

interface CompanySelectorProps {
  onSelectCompany: (id: string, name: string) => void;
  selectedCompanyId?: string;
  required?: boolean;
}

export const CompanySelector = ({ onSelectCompany, selectedCompanyId, required = false }: CompanySelectorProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [companies, setCompanies] = useState<Company[]>([]);
  const [recentCompanies, setRecentCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateCompanyPopupOpen, setIsCreateCompanyPopupOpen] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      if (open) {
        setIsLoading(true);
        setError(null);
        
        try {
          let query = supabase
            .from('companies')
            .select('id, name');
          
          if (searchTerm) {
            query = query.ilike('name', `%${searchTerm}%`);
          }
          
          const { data, error } = await query.limit(10);
          
          if (error) {
            console.error("Error fetching companies:", error);
            setError("Failed to load companies");
            setCompanies([]);
            return;
          }
          
          setCompanies(data || []);
        } catch (error) {
          console.error("Failed to fetch companies:", error);
          setError("An unexpected error occurred");
          setCompanies([]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    const fetchRecentCompanies = async () => {
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('id, name')
          .order('created_at', { ascending: false })
          .limit(5);
        
        if (error) {
          console.error("Error fetching recent companies:", error);
          return;
        }
        
        setRecentCompanies(data || []);
      } catch (error) {
        console.error("Failed to fetch recent companies:", error);
      }
    };

    if (open) {
      fetchCompanies();
      fetchRecentCompanies();
    }
  }, [searchTerm, open]);

  useEffect(() => {
    const fetchSelectedCompany = async () => {
      if (selectedCompanyId) {
        try {
          const { data, error } = await supabase
            .from('companies')
            .select('id, name')
            .eq('id', selectedCompanyId)
            .single();
          
          if (error) {
            console.error("Error fetching selected company:", error);
            return;
          }
          
          if (data) {
            setSelectedCompany(data);
            setSearchTerm(data.name);
          }
        } catch (error) {
          console.error("Failed to fetch selected company:", error);
        }
      }
    };

    if (!selectedCompany && selectedCompanyId) {
      fetchSelectedCompany();
    }
  }, [selectedCompanyId, selectedCompany]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data && event.data.type === "company-created" && event.data.company) {
        // Optionally, fetch the company from the backend to get the ID
        setSelectedCompany(event.data.company);
        setSearchTerm(event.data.company.name);
        onSelectCompany(event.data.company.id, event.data.company.name);
        setOpen(false);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onSelectCompany]);

  const handleCreateCompany = async (companyData: any) => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .insert([companyData])
        .select()
        .single();

      if (error) {
        console.error("Error creating company:", error);
        return false;
      }

      if (data) {
        setIsCreateCompanyPopupOpen(false);
        setSelectedCompany(data);
        setSearchTerm(data.name);
        onSelectCompany(data.id, data.name);
        return true;
      }
    } catch (error) {
      console.error("Failed to create company:", error);
      return false;
    }
    return false;
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between"
            aria-required={required}
          >
            {selectedCompany?.name || searchTerm || t("companySelector.selectCompanyPlaceholder")}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" style={{ width: triggerRef.current ? triggerRef.current.offsetWidth : undefined }}>
          <Command>
            <CommandInput 
              placeholder={t("companySelector.searchCompanyPlaceholder")}
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              {isLoading ? (
                <div className="py-6 text-center text-sm text-gray-500">
                  Loading companies...
                </div>
              ) : error ? (
                <div className="py-6 text-center text-sm text-red-500">
                  {error}
                </div>
              ) : (
                <>
                  <CommandGroup>
                    {(searchTerm ? companies : recentCompanies).map((company) => (
                      <CommandItem
                        key={company.id}
                        value={company.name}
                        onSelect={() => {
                          onSelectCompany(company.id, company.name);
                          setSelectedCompany(company);
                          setSearchTerm(company.name);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCompanyId === company.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {company.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                  <CommandEmpty>
                    <div className="py-6 text-center">
                      <p className="text-sm text-gray-500 mb-4">{t("companySelector.noCompaniesFound")}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Open popup window for company creation
                          const w = 600, h = 700;
                          const left = window.screenX + (window.outerWidth - w) / 2;
                          const top = window.screenY + (window.outerHeight - h) / 2;
                          popupRef.current = window.open(
                            "/add-company-popup",
                            "AddCompanyPopup",
                            `width=${w},height=${h},left=${left},top=${top},resizable,scrollbars`
                          );
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {t("companySelector.createCompanyButton")}
                      </Button>
                    </div>
                  </CommandEmpty>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Company Creation Popup */}
      <HeadlessPopover open={isCreateCompanyPopupOpen} onOpenChange={setIsCreateCompanyPopupOpen}>
        <PopoverContent className="w-[500px] p-4">
          <AddCompanyForm
            isOpen={true}
            onClose={() => setIsCreateCompanyPopupOpen(false)}
            onSubmit={handleCreateCompany}
          />
        </PopoverContent>
      </HeadlessPopover>
    </>
  );
};
